import { z } from "zod";
// True primitives shared by every incidentOverview workflow family (cost-estimate,
// damage-appraisal, invoice). See ./costEstimateDamageAppraisal.ts and ./invoice.ts for the
// workflow-specific schemas built on top of these.
/**
 * The flat wire-shaped container a parser produces. `data` is `null` (with `hasLoadError: false`)
 * when the container legitimately carries nothing. Folded into the tagged view `OverviewContainer`
 * by `toContainer` at the projection boundary.
 */
export const overviewContainerSchema = z.object({
    data: z.record(z.string(), z.unknown()).nullable(),
    hasLoadError: z.boolean(),
});
export const overviewCommentSchema = z.object({
    id: z.string(),
    content: z.string(),
    created: z.string().nullable(),
    owner: z.string(),
    ownerName: z.string().nullable(),
    editor: z.string(),
    editorName: z.string().nullable(),
});
// `data` is always present per adapter contract — only the top-level container may nullify it.
export const overviewSectionSchema = z.object({
    id: z.string(),
    created: z.string().nullable(),
    data: z.record(z.string(), z.unknown()),
});
export const overviewDocumentSchema = z.object({
    name: z.string(),
    url: z.url({ protocol: /^https$/ }),
});
/** `hasLoadErrors` (plural) is namespace-level — distinct from the per-container `hasLoadError`. */
export const overviewDocumentsSchema = z.object({
    items: z.array(overviewDocumentSchema),
    hasLoadErrors: z.boolean(),
});
// `UNTOUCHED` maps to the literal string 'undefined' — not the JS undefined value.
export const containerMetaStatusSchema = z.enum([
    "IN_EDITING",
    "undefined",
    "INACTIVE_LINK",
    "PENDING",
    "ACTIVE",
    "IN_EDITING_UNCOMMENTED",
    "CLOSED_FINISHED",
    "CLOSED_UNFINISHED",
    "CLOSED_IRRELEVANT",
    "CHECKING",
    "DEACTIVATED",
    "INIT",
    "HIGHLIGHT",
]);
export const ContainerMetaStatusValues = containerMetaStatusSchema.enum;
/** The tracking metadata an enriched container delivery carries alongside its data. */
export const containerMetaFieldsSchema = z.object({
    metaStatus: containerMetaStatusSchema.nullable(),
    metaHasLoadError: z.boolean(),
    comments: z.array(overviewCommentSchema),
    commentsHasLoadError: z.boolean(),
});
// ─── The canonical, tagged view shapes ────────────────────────────────────────────────────────
// Single source of truth for the view types every workflow projects onto (see ./view.ts's
// `OverviewView`). Each facet that can fail independently of its payload is a `z.discriminatedUnion`
// on `kind`, so a loaded payload can never sit beside a load-error flag. Folded from the flat raw
// wire shapes above by `metaOf` / `toContainer` / `toSectionsContainer` at the projection boundary.
/**
 * The attached-document count: `unsupported` when the workflow's adapter emits no document data
 * at all (e.g. invoice), `failed` when a count was expected but errored, `loaded` for a real count.
 */
export const documentCountStateSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("unsupported") }),
    z.object({ kind: z.literal("failed") }),
    z.object({ kind: z.literal("loaded"), count: z.number() }),
]);
/**
 * A container's lifecycle status: `failed` when the status itself errored, `loaded` otherwise —
 * `status` may still be `null` there, a legitimately-absent status distinct from a load failure.
 */
export const statusStateSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("failed") }),
    z.object({ kind: z.literal("loaded"), status: containerMetaStatusSchema.nullable() }),
]);
/**
 * A container's comment thread. No information is lost on `failed`: the comment feed already
 * discards the item list entirely on a load error, and a partial parse resolves to `failed` too.
 */
export const commentsStateSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("failed") }),
    z.object({ kind: z.literal("loaded"), comments: z.array(overviewCommentSchema) }),
]);
/**
 * A container's tracking metadata — lifecycle status, comment thread, and attached-document
 * count.
 */
export const containerMetaSchema = z.object({
    status: statusStateSchema,
    comments: commentsStateSchema,
    documentCount: documentCountStateSchema,
});
/** A container's `data`, or its load failure. */
export const containerDataSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("failed") }),
    z.object({ kind: z.literal("loaded"), data: z.record(z.string(), z.unknown()).nullable() }),
]);
/** A plain container: its opaque `data` record, tagged with its load outcome. */
export const overviewContainerViewSchema = z.object({
    content: containerDataSchema,
});
/**
 * A meta-capable container. `meta` is absent when the workflow delivers the container without
 * tracking metadata — consumers that need the metadata (chips, comment feeds) read its absence
 * as "not tracked in this workflow", while `content` reads work either way.
 */
export const overviewMetaContainerSchema = overviewContainerViewSchema.extend({
    meta: containerMetaSchema.optional(),
});
/** The sections list, or its load failure. */
export const sectionsStateSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("failed") }),
    z.object({ kind: z.literal("loaded"), sections: z.array(overviewSectionSchema) }),
]);
/**
 * A sections-based container: repeated dated entries instead of a single `data` record.
 * `hasLoadError` is deliberately left a bare boolean rather than tagged: the envelope carries no
 * payload of its own (that's `sections`), so tagging it would produce a union with an empty
 * `loaded` arm.
 */
export const overviewSectionsContainerSchema = z.object({
    hasLoadError: z.boolean(),
    sections: sectionsStateSchema,
    meta: containerMetaSchema,
});
const FAILED_CONTAINER = { data: null, hasLoadError: true };
/** A malformed container collapses to a load error rather than failing the whole parse. */
export function parseContainer(raw) {
    const parsed = overviewContainerSchema.safeParse(raw);
    return parsed.success ? parsed.data : FAILED_CONTAINER;
}
/** A legitimate `null` (confirmed absent) is preserved; a malformed container collapses to a load error — distinct from absent. */
export function parseNullableContainer(raw) {
    const parsed = overviewContainerSchema.nullable().safeParse(raw);
    return parsed.success ? parsed.data : FAILED_CONTAINER;
}
/** Parse each element individually and keep the ones that succeed; a single invalid item flags
 *  the load error without discarding the valid siblings. Mirrors `parseDocuments`. */
export function parseArrayItems(schema, raw) {
    if (!Array.isArray(raw))
        return { items: [], hasErrors: true };
    const items = [];
    let dropped = false;
    for (const item of raw) {
        const parsed = schema.safeParse(item);
        if (parsed.success)
            items.push(parsed.data);
        else
            dropped = true;
    }
    return { items, hasErrors: dropped };
}
function foldDocumentCount(count, hasLoadError) {
    if (hasLoadError === true)
        return { kind: "failed" };
    if (count === undefined)
        return { kind: "unsupported" };
    return { kind: "loaded", count };
}
function foldStatus(status, hasLoadError) {
    return hasLoadError ? { kind: "failed" } : { kind: "loaded", status };
}
/** A partial parse (some comments dropped) still resolves to `failed`, matching the old `commentsHasLoadError`. */
function foldComments(comments, hasLoadError) {
    return hasLoadError ? { kind: "failed" } : { kind: "loaded", comments };
}
/** Extracts the tracking metadata from an enriched container's raw wire fields. */
export function metaOf(raw) {
    return {
        status: foldStatus(raw.metaStatus, raw.metaHasLoadError),
        comments: foldComments(raw.comments, raw.commentsHasLoadError),
        documentCount: foldDocumentCount(raw.documentCount, raw.documentCountHasLoadError),
    };
}
/** Folds a raw wire container into its tagged view shape. */
export function toContainer(raw) {
    return { content: raw.hasLoadError ? { kind: "failed" } : { kind: "loaded", data: raw.data } };
}
export function toMetaContainer(container) {
    return { content: toContainer(container).content, meta: metaOf(container) };
}
export function toSectionsContainer(container) {
    return {
        hasLoadError: container.hasLoadError,
        sections: container.sectionsHasLoadError ? { kind: "failed" } : { kind: "loaded", sections: container.sections },
        meta: metaOf(container),
    };
}
